import {
  BackButtonPriority,
  DefaultProgressColor,
  DefaultProgressColorName,
  ExitAlertCount,
  ISODateFormat,
  MaxUploadImageSizeMB,
  MultiSubjectName,
  ProgressColorNames,
  ProgressColors,
  ProgressThresholds,
  VimeoBaseUrl,
  VimeoOembedUrl,
  VimeoThumbnailWidth,
  VimeoVideoBaseUrl,
  YOUTUBE_VIDEO_ID_LENGTH,
  YOUTUBE_WATCH_BASE_URL,
  YoutubeBaseUrl,
  YoutubeQueryParams,
  YoutubeThumbnailBaseUrl,
  YoutubeUrlRegex,
} from "@/constants/global";
import { NotificationPositionMap } from "@/constants/inputs";
import { AllStudentRoleIds, AllTeachingRoleIds } from "@/constants/userRoles";
import { DevicePlatformsEnum } from "@/enums/appSettings";
import { VideoPlatformEnum } from "@/enums/content";
import { LoginDeviceTypeIdsEnum, MimeTypesEnum } from "@/enums/global";
import router from "@/routerInstance";
import { useAppSettingsStore } from "@/stores/core/appSettingsStore";
import { useSessionStore } from "@/stores/core/sessionStore";
import { type ComponentColorType } from "@/types/componentType";
import type { UploaderFile } from "@/types/fileUpload";
import {
  AzureUploadUrl,
  CustomMessage,
  CustomNotification,
  CustomToast,
  CustomTreeNode,
  IdNameChildren,
  IdNamePair,
  InputOption,
  PasswordStrengthTier,
} from "@/types/global";
import { Clipboard } from "@capacitor/clipboard";
import { FileTransfer } from "@capacitor/file-transfer";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Keyboard } from "@capacitor/keyboard";
import { loadingController, toastController, useBackButton, useIonRouter } from "@ionic/vue";
import { RouteAction, RouteDirection, RouteInfo } from "@ionic/vue-router/dist/types/types";
import axios from "axios";
import imageCompression from "browser-image-compression";
import {
  differenceInDays,
  endOfWeek,
  format,
  intervalToDuration,
  parseISO,
  startOfWeek,
} from "date-fns";
import { fromZonedTime, getTimezoneOffset } from "date-fns-tz";
import filenamify from "filenamify";
import { close } from "ionicons/icons";
import _ from "lodash";
import { storeToRefs } from "pinia";
import * as SparkMD5 from "spark-md5";
import { ref, Ref } from "vue";
import { useRoute } from "vue-router";
import * as yup from "yup";

type IonRouterInstance = ReturnType<typeof useIonRouter>;

// Mongo ObjectId guard — 24-char lowercase hex (^[a-f0-9]{24}$). Validate every
// *DesignId before submit and on deserialize (content-v2 two-step content design;
// load-bearing because some server-side validators are still missing).
const OBJECT_ID_REGEX = /^[a-f0-9]{24}$/;

export function isValidObjectId(value: string | null | undefined): boolean {
  return typeof value === "string" && OBJECT_ID_REGEX.test(value);
}

// Logout is a teardown that happens to end in a navigation, not the other way round. During an
// auth cascade every in-flight authenticated request discovers the dead session within a few ms
// of the others; each one used to start its own router.replace, and each replace cancelled the
// previous navigation before its guard could finish — so the Logout view never mounted and the
// teardown that clears the token never ran.
//
// Both halves of the fix hang off dropping the credentials FIRST:
//   - The navigation can still be cancelled — by a concurrent guard, by Ionic, by anything — but
//     once IsLoggedIn is false every pending guard and every mounted component stops issuing
//     authenticated calls, so the cascade starves instead of re-feeding itself.
//   - It doubles as the re-entrancy guard. Deriving that from session state rather than a
//     module flag means there is no latch to leave stuck closed if the teardown never completes,
//     and it re-arms by itself the moment a new session exists.
// Identity fields survive the drop — the Logout view still needs them for expireLoginSession.
// Every caller reaches this from an authenticated context, so none is skipped by the guard.
export async function gotoLogout(isSystemExpired: boolean) {
  const sessionStore = useSessionStore();
  if (!sessionStore.IsLoggedIn) {
    return;
  }
  sessionStore.clearCredentials();
  await router.replace({ name: "Logout", params: { isSystemExpired: String(isSystemExpired) } });
}

export function gotoDashboard() {
  const sessionStore = useSessionStore();
  router.replace({ name: sessionStore.UserInfo.UserDashboard });
}

// Captured once at app boot (App.vue setup). useIonRouter() needs an injection context, so plain
// functions and Options-API stores (no setup context) reach the Ionic router through this ref.
let registeredIonRouter: IonRouterInstance | null = null;

export function registerIonRouter(ionRouter: IonRouterInstance): void {
  registeredIonRouter = ionRouter;
}

// Ionic-aware back core, shared by gotoPrevious() and useAppNavigation().navigateBack() so the
// two paths cannot drift. A raw router.back() bypasses Ionic's locationHistory: inside a nested
// outlet, or after a page reload (which resets locationHistory to a single entry), the URL
// changes but the outlet keeps the leaving view mounted-hidden, dead-ending the next forward
// navigation to the same route. Pop the Ionic stack when it can; else a tracked back-transition
// replace keeps locationHistory in sync and unmounts the leaving view; else dashboard.
function ionRouterBack(
  ionRouter: IonRouterInstance,
  fallbackTo: Parameters<IonRouterInstance["navigate"]>[0] | null,
): void {
  if (ionRouter.canGoBack()) {
    ionRouter.back();
  } else if (fallbackTo !== null) {
    ionRouter.navigate(fallbackTo, "back", "replace");
  } else {
    gotoDashboard();
  }
}

export function gotoPrevious() {
  if (registeredIonRouter === null) {
    // Boot-order guard: called before App.vue registered the ion router — keep the legacy
    // raw-history behavior rather than crash.
    router.back();
    return;
  }
  // vue-router mirrors the previous in-app URL into history.state.back; unlike Ionic's
  // locationHistory it survives a reload and never points off-origin. It is null on a true
  // cold deep-link, where the dashboard is the only sane destination.
  const previousUrl = router.options.history.state.back;
  // The fallback back-replace INHERITS the replaced entry's `back` pointer (vue-router's
  // replace keeps it), so after one fallback state.back names the URL already on screen.
  // Replaying it would short-circuit as NAVIGATION_DUPLICATED and dead-end every further
  // back press (incl. Android hardware back) — treat self-reference as exhausted history.
  const hasUsablePreviousUrl =
    typeof previousUrl === "string" && previousUrl !== router.currentRoute.value.fullPath;
  ionRouterBack(registeredIonRouter, hasUsablePreviousUrl ? previousUrl : null);
}

export function useAppNavigation() {
  const ionRouter = useIonRouter();
  function navigateTo(
    to: Parameters<IonRouterInstance["navigate"]>[0],
    direction: RouteDirection = "forward",
    action: RouteAction = "push",
  ) {
    ionRouter.navigate(to, direction, action);
  }
  // Ionic-aware back with an explicit fallback (see ionRouterBack above for why raw
  // router.back() is never used).
  // Resolves once the navigation settles (router.afterEach). The beforeEach guard's
  // afterNavigateValidated() dismisses any open modal, so a caller that opens a modal right
  // after going back MUST await this — otherwise the in-flight guard dismisses the new modal.
  function navigateBack(fallbackTo: Parameters<IonRouterInstance["navigate"]>[0]): Promise<void> {
    const navigationSettled = new Promise<void>((resolve) => {
      const removeAfterEach = router.afterEach(() => {
        removeAfterEach();
        resolve();
      });
    });
    ionRouterBack(ionRouter, fallbackTo);
    return navigationSettled;
  }
  return { navigateTo, navigateBack };
}

// Router desync safety net — the browser-chrome sibling of the gotoPrevious() fix above.
// A browser back/forward after a page reload is a raw popstate no app code can intercept:
// @ionic/vue-router classifies it as a pop with direction "none" (the reloaded entry has no
// pushedByRoute), keeps the leaving view mounted-hidden, and on the next push to that same
// route computes leaving URL === entering URL and skips its bookkeeping entirely, leaving
// currentRouteInfo stale. The outlet then sees the "entering" view already visible and
// early-returns: the URL commits but the screen never changes.
interface NavManagerSlice {
  getCurrentRouteInfo: () => RouteInfo | undefined;
}

// Marker query for the recovery bounce; on screen for at most a frame, then replaced away.
const ROUTER_RESYNC_MARKER = "__ionicResync=1";

// Consecutive recovery attempts allowed before the safety net stands down. Each recovery issues
// two more router.replace calls, so an environment where recovery cannot succeed — an auth
// cascade cancelling navigations faster than a guard can finish — turns the net into an
// amplifier. Any navigation Ionic did ingest resets the budget; the cap only bites on a run of
// consecutive failures, which is the only case a desync recovery was ever meant to handle.
const MAX_CONSECUTIVE_RESYNC_RECOVERIES = 3;

function isIonRouteInfoInSync(
  navManager: NavManagerSlice,
  route: { path: string; fullPath: string },
): boolean {
  const routeInfo = navManager.getCurrentRouteInfo();
  if (!routeInfo || !routeInfo.pathname) {
    // Nothing processed yet (pre-first-navigation) — nothing to compare against.
    return true;
  }
  // Same formulas @ionic/vue-router uses to build routeInfo (pathname = location.path,
  // search = fullPath.split("?")[1] || ""), so a mismatch can only mean Ionic skipped
  // this navigation.
  const expectedSearch = route.fullPath.split("?")[1] ?? "";
  return routeInfo.pathname === route.path && (routeInfo.search ?? "") === expectedSearch;
}

// App.vue installs this at boot with the raw "navManager" provide (the public useIonRouter()
// surface does not expose getCurrentRouteInfo). Accepts unknown so the caller needs no cast.
export function installRouterDesyncSafetyNet(injectedNavManager: unknown): void {
  const navManager = injectedNavManager as NavManagerSlice | undefined;
  if (!navManager || typeof navManager.getCurrentRouteInfo !== "function") {
    return;
  }
  let recoveryInFlight = false;
  let consecutiveRecoveries = 0;
  // Runs after @ionic/vue-router's own afterEach (registered at router creation), so by now
  // Ionic has either ingested this navigation or bailed and left routeInfo stale.
  router.afterEach(async (to, _from, failure) => {
    if (failure || recoveryInFlight) {
      return;
    }
    if (isIonRouteInfoInSync(navManager, to)) {
      consecutiveRecoveries = 0;
      return;
    }
    if (consecutiveRecoveries >= MAX_CONSECUTIVE_RESYNC_RECOVERIES) {
      return;
    }
    const stalePathname = navManager.getCurrentRouteInfo()?.pathname;
    if (!stalePathname) {
      return;
    }
    consecutiveRecoveries += 1;
    // The stale routeInfo names the page still on screen. Recover with a two-replace bounce:
    // first to that page's path plus a marker query — the changed fullPath forces Ionic to
    // re-ingest it while the outlet no-ops (that view is already visible) — then back to the
    // intended URL. The second replace now has a distinct leaving entry, so the outlet runs a
    // real (instant, direction "none") transition that unhides the target view and unmounts
    // the stale one.
    recoveryInFlight = true;
    try {
      const staleSearch = navManager.getCurrentRouteInfo()?.search;
      const markerSearch = staleSearch ? `${staleSearch}&` : "";
      await router.replace(`${stalePathname}?${markerSearch}${ROUTER_RESYNC_MARKER}`);
      await router.replace(to.fullPath);
    } finally {
      recoveryInFlight = false;
    }
    // Compared against the *settled* route, not `to`, so a user navigation racing the
    // recovery is not misread as a failure.
    if (
      consecutiveRecoveries === 1 &&
      !isIonRouteInfoInSync(navManager, router.currentRoute.value)
    ) {
      // Unreachable by the traced bookkeeping; last resort — a reload rebuilds Ionic's
      // locationHistory from the committed URL, which always renders. Restricted to the first
      // attempt of a run: a reload resets this module's state, so letting a repeated failure
      // reload would be a reload loop rather than a rescue.
      window.location.reload();
    }
  });
}

export function getAzureUrl(
  partialUrl: string,
  randomize: boolean = false,
  includeSession: boolean = true,
): string {
  let fullUrl = partialUrl;
  if (!(fullUrl.startsWith("http://") || fullUrl.startsWith("https://"))) {
    const appSettingsStore = useAppSettingsStore();
    const { AzureBaseUrl } = storeToRefs(appSettingsStore);
    if (partialUrl.startsWith("~")) {
      fullUrl = partialUrl.replace("~", AzureBaseUrl.value);
    } else {
      fullUrl = `${AzureBaseUrl.value}/${partialUrl}`;
    }
  }
  const sessionStore = useSessionStore();
  const { UserInfo } = storeToRefs(sessionStore);
  const randomString = randomize ? generateRandomString(8) : "";
  const sessionUuid = `${UserInfo.value.SessionUuid}${randomString}`;
  return includeSession && sessionUuid ? `${fullUrl}?session=${sessionUuid}` : fullUrl;
}

export function getImageUrl(imageName: string): string {
  return getAzureUrl(`~/webapp/assets/images/${imageName}`);
}

export function getImageAsset(imageName: string): string {
  return `/assets/images/${imageName}`;
}

export function getImageSrc(imageUrl: string, randomize: boolean = false): string {
  return imageUrl.startsWith("data:") ? imageUrl : getAzureUrl(imageUrl, randomize);
}

export function getIconUrl(iconName: string): string {
  return getAzureUrl(`~/webapp/assets/icons/${iconName}.svg`);
}

// A subject icon's file name: the subject name lower-cased with everything except letters and
// digits removed — spaces, tabs, newlines, "/", "\", "-", "&"… ("Mental Ability" → "mentalability").
// Unicode classes rather than [a-z0-9] so a name in a non-Latin script keeps its letters, and \p{M}
// so Devanagari vowel signs (combining marks, not letters) survive — "हिंदी" stays "हिंदी".
export function getSubjectIconName(subjectName: string): string {
  return subjectName.replace(/[^\p{L}\p{M}\p{N}]/gu, "").toLowerCase();
}

export function getSubjectIconUrl(subjectName: string, isColor: boolean): string {
  if (subjectName === undefined) {
    subjectName = MultiSubjectName;
  }
  const iconName = getSubjectIconName(subjectName);
  const iconType = isColor ? "color" : "wireframe";
  return getAzureUrl(`~/webapp/assets/icons/subjects/${iconType}/${iconName}.svg`);
}

export function getCourseIconUrl(courseName: string, isColor: boolean): string {
  const iconName = courseName.replaceAll(" ", "-").toLowerCase().toString();
  const iconType = isColor ? "color" : "wireframe";
  return getAzureUrl(`~/webapp/assets/icons/courses/${iconType}/${iconName}.svg`);
}

// A view's MenuIcon — configured on the admin navbar-config screen and used by both the side menu
// and the footer tab — holds a Tabler icon name. Accept a name with or without the `ti-` prefix,
// so a value pasted
// straight from tabler.io renders the same as a hand-typed one; an unset field yields no classes,
// which is what lets a caller skip the <i> entirely rather than render an empty glyph box.
export function getTablerIconClass(iconName: string | null | undefined): string {
  const name = (iconName ?? "").trim().replace(/^ti-/, "");
  return name ? `ti ti-${name}` : "";
}

export function getAlphabetIconUrl(userName: string, isUppercase: boolean): string {
  let iconName: string | null = userName.replaceAll(" ", "").toLowerCase().toString();
  iconName = iconName.length > 0 ? iconName[0] : null;
  if (iconName === null || !/[a-zA-Z]/g.test(iconName)) {
    iconName = "x";
  }
  const iconType = isUppercase ? "uppercase" : "lowercase";
  return getAzureUrl(`~/webapp/assets/icons/alphabets/${iconType}/${iconName}.svg`);
}

export function getVimeoVimeoUrl(vimeoId: string): string {
  return `${VimeoBaseUrl}${vimeoId}`;
}

export function getVideoUrlForPlatform(videoPlatformId: number | null, videoId: string): string {
  return videoPlatformId === VideoPlatformEnum.YOUTUBE
    ? `${YOUTUBE_WATCH_BASE_URL}${videoId}`
    : getVimeoVimeoUrl(videoId);
}

// The id is capture group 1 of YoutubeUrlRegex (every url-shape alternative is non-capturing).
// Optional chaining because a match with an empty tail (e.g. a bare ".../watch?") leaves the
// group as "" and a future shape could leave it undefined outright.
export function getYoutubeVideoIdFromUrl(videoUrl: string): string | undefined {
  const videoId = videoUrl.match(YoutubeUrlRegex)?.[1];
  return videoId?.length === YOUTUBE_VIDEO_ID_LENGTH ? videoId : undefined;
}

export function getVideoEmbedUrlForVimeoId(vimeoId: string, isThumbnail: boolean): string {
  const vimeoUrl = getVimeoVimeoUrl(vimeoId);
  return getVideoEmbedUrl(vimeoUrl, isThumbnail);
}

export function getVideoEmbedUrl(videoUrl: string, isThumbnail: boolean): string {
  const youtubeId = getYoutubeVideoIdFromUrl(videoUrl);
  const vimeoProps = isThumbnail ? "?background=1&autoplay=0" : "?title=0&byline=0";
  return youtubeId
    ? `${YoutubeBaseUrl}${youtubeId}?${YoutubeQueryParams}`
    : `${videoUrl}${vimeoProps}`;
}

export function getVimeoIdFromUrl(videoUrl: string): string | null {
  const match = videoUrl.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return match ? match[1] : null;
}

export async function getVimeoThumbnailUrl(vimeoId: string): Promise<string | null> {
  try {
    const videoUrl = `${VimeoVideoBaseUrl}${vimeoId}`;
    const requestUrl = `${VimeoOembedUrl}?url=${encodeURIComponent(videoUrl)}&width=${VimeoThumbnailWidth}`;
    const response = await fetch(requestUrl);
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    return data ? data.thumbnail_url : null;
  } catch {
    return null;
  }
}

export async function getVideoThumbnailUrl(videoUrl: string): Promise<string | null> {
  const youtubeId = getYoutubeVideoIdFromUrl(videoUrl);
  if (youtubeId) {
    return `${YoutubeThumbnailBaseUrl}${youtubeId}/hqdefault.jpg`;
  }
  const vimeoId = getVimeoIdFromUrl(videoUrl);
  return vimeoId ? getVimeoThumbnailUrl(vimeoId) : null;
}

export function getRandomInt(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function copyTextWithLegacyFallback(textToCopy: string): boolean {
  const textArea = document.createElement("textarea");
  textArea.value = textToCopy;
  textArea.setAttribute("readonly", "");
  textArea.classList.add("fixed", "opacity-0", "pointer-events-none");
  document.body.appendChild(textArea);
  textArea.select();
  try {
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    document.body.removeChild(textArea);
  }
}

async function writeTextToClipboard(textToCopy: string): Promise<boolean> {
  const appSettingsStore = useAppSettingsStore();
  if (appSettingsStore.IsNativePlatform) {
    try {
      await Clipboard.write({ string: textToCopy });
      return true;
    } catch {
      return false;
    }
  }
  // navigator.clipboard is undefined outside a secure context (plain http, some webviews)
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(textToCopy);
      return true;
    } catch {
      // permission denied or unsupported — fall through to the legacy path
    }
  }
  return copyTextWithLegacyFallback(textToCopy);
}

export async function copyToClipboard(textToCopy: string | null, textDescription: string) {
  if (textToCopy === null) {
    return;
  }
  const isCopied = await writeTextToClipboard(textToCopy);
  await presentToast({
    toastIndex: 0,
    messageText: isCopied
      ? `${textDescription} copied to clipboard!`
      : `Could not copy ${textDescription}. Please copy it manually.`,
    toastColor: isCopied ? "success" : "danger",
    duration: 3,
    position: "top",
  });
}

export async function pasteImageFromClipboard() {
  if (!navigator.clipboard?.read) {
    await presentToast({
      toastIndex: 0,
      messageText: "Pasting from the clipboard is not available on this device.",
      toastColor: "danger",
      duration: 3,
      position: "top",
    });
    return;
  }
  const clipboardItems = await navigator.clipboard.read();
  for (let i = 0, len = clipboardItems.length; i < len; i++) {
    if (clipboardItems[i].types.includes(MimeTypesEnum.PNG)) {
      const clipboardData = await clipboardItems[i].getType(MimeTypesEnum.PNG);
      const encodedFile = await convertBlobToBase64(clipboardData);
      return encodedFile;
    }
  }
}

export function convertMinutesToDuration(
  timeInMinutes: number | undefined,
  minimumThreshold: number = 1,
  shortVersion: boolean = false,
): string | undefined {
  if (timeInMinutes === undefined || timeInMinutes <= minimumThreshold) {
    return;
  }
  return convertSecondsToDuration(timeInMinutes * 60, minimumThreshold * 60, shortVersion);
}

export function convertSecondsToDuration(
  timeInSeconds: number | undefined,
  minimumThreshold: number = 60,
  shortVersion: boolean = false,
): string | undefined {
  if (timeInSeconds === undefined || timeInSeconds <= minimumThreshold) {
    return;
  }
  const roundedTimeInSecs = minimumThreshold * Math.floor(timeInSeconds / minimumThreshold);
  interface durationDictionary {
    [key: string]: number;
  }
  const duration = intervalToDuration({
    start: 0,
    end: roundedTimeInSecs * 1000,
  }) as durationDictionary;
  Object.keys(duration).forEach((key) => {
    if (duration[key] === 0) {
      delete duration[key];
    }
  });
  const formttedDuration = Object.keys(duration)
    .map((key) => {
      const timeName = shortVersion ? key[0] : ` ${duration[key] === 1 ? key.slice(0, -1) : key}`;
      return `${duration[key]}${timeName}`;
    })
    .join(" ");
  return formttedDuration;
}

export type SearchTarget = string | number | null | undefined;

function splitSearchWords(searchText: string | null | undefined): Array<string> {
  if (!searchText) {
    return [];
  }
  return searchText
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter((word) => word !== "");
}

// Multi-word, order-independent partial match: true when EVERY whitespace-separated
// word of searchText is a substring of at least one target. An empty/whitespace-only
// query matches everything; numbers are stringified so ids and roll numbers are
// searchable. AND across words, OR across targets — a word may match any target.
export function matchSearchWords(
  targets: SearchTarget | Array<SearchTarget>,
  searchText: string | null | undefined,
): boolean {
  const searchWords = splitSearchWords(searchText);
  if (searchWords.length === 0) {
    return true;
  }
  const normalizedTargets = (Array.isArray(targets) ? targets : [targets])
    .filter((target): target is string | number => target !== null && target !== undefined)
    .map((target) => String(target).toLowerCase());
  if (normalizedTargets.length === 0) {
    return false;
  }
  return searchWords.every((word) => normalizedTargets.some((target) => target.includes(word)));
}

// Multi-word filter for flat arrays; getSearchTargets extracts the searchable values
// (top-level, nested, or computed) from each item. Empty query returns the array as-is.
export function filterArrayBySearchWords<T>(
  items: Array<T>,
  getSearchTargets: (item: T) => SearchTarget | Array<SearchTarget>,
  searchText: string | null | undefined,
): Array<T> {
  if (splitSearchWords(searchText).length === 0) {
    return items;
  }
  return items.filter((item) => matchSearchWords(getSearchTargets(item), searchText));
}

export function matchUnknownToSearchText(inputValue: string | any | null, searchText: string) {
  if (inputValue === null) {
    return false;
  }
  searchText = searchText.trim().toLowerCase();
  let inputValueString;
  if (typeof inputValue === "string") {
    inputValueString = inputValue.trim().toLowerCase();
  } else if (typeof inputValue === "number") {
    inputValueString = String(inputValue).toLowerCase();
  } else if (typeof inputValue === "object") {
    const nameKey = "Name" as keyof typeof inputValue;
    if (inputValue[nameKey] !== undefined) {
      inputValueString = inputValue[nameKey].toString().trim().toLowerCase();
    }
  }
  return inputValueString?.includes(searchText) ?? false;
}

export function filterNestedObjectArrayBySearchText(
  originalArray: Array<any> | null,
  filterFields: Array<string>,
  searchText: string | null,
) {
  if (!originalArray) {
    return originalArray;
  }
  let filteredArray = originalArray;
  if (searchText === null || searchText === undefined || searchText.trim() === "") {
    return filteredArray;
  }
  const searchParts = splitSearchWords(searchText);
  if (searchParts.length === 0) {
    return filteredArray;
  }
  filteredArray = filteredArray.filter((item) => {
    let isMatched = true;
    for (let s = 0; s < searchParts.length; s++) {
      let partMatched = false;
      for (let f = 0; f < filterFields.length; f++) {
        const itemFieldValue = item[filterFields[f]];
        if (itemFieldValue === null) {
          continue;
        }
        if (Array.isArray(itemFieldValue)) {
          for (let c = 0; c < itemFieldValue.length; c++) {
            if (matchUnknownToSearchText(itemFieldValue[c], searchParts[s])) {
              partMatched = true;
              break;
            }
          }
        } else if (matchUnknownToSearchText(itemFieldValue, searchParts[s])) {
          partMatched = true;
          break;
        }
      }
      if (!partMatched) {
        isMatched = false;
        break;
      }
    }
    return isMatched;
  });
  return filteredArray;
}

export function sortNestedObjectArrayByField(
  originalArray: Array<any> | null,
  sortField: string,
  isDescendingSort: boolean,
) {
  if (!originalArray || !sortField) {
    return originalArray;
  }
  const sortedArray = [...originalArray];
  sortedArray.sort((a, b) => {
    let valA: any = a[sortField];
    let valB: any = b[sortField];
    if (isInputOption(valA) && isInputOption(valB)) {
      valA = valA.Name;
      valB = valB.Name;
    }
    if (valA == null && valB == null) {
      return 0;
    } else if (valA == null || valB == null) {
      return (valA == null ? -1 : 1) * (isDescendingSort ? -1 : 1);
    } else {
      const comparison = valA.toString().localeCompare(valB.toString(), undefined, {
        numeric: true,
        sensitivity: "base",
      });
      return isDescendingSort ? -comparison : comparison;
    }
  });
  return sortedArray;
}

export function isInputOption(value: any): value is InputOption {
  return value !== null && typeof value === "object" && "Id" in value && "Name" in value;
}

export function convertToTreeData(
  data: Array<object>,
  treeLevels: Array<string>,
): Array<IdNameChildren> {
  const idNameChildren: Array<IdNameChildren> = [];
  for (let i = 0; i < data.length; i++) {
    const currentIdName = data[i] as IdNamePair;
    const typedContentType = treeLevels[0] as keyof (typeof data)[number];
    if (treeLevels.length > 0 && data[i][typedContentType] !== undefined) {
      const children = convertToTreeData(data[i][typedContentType], treeLevels.slice(1));
      idNameChildren.push({ Id: currentIdName.Id, Name: currentIdName.Name, Children: children });
    } else {
      idNameChildren.push({ Id: currentIdName.Id, Name: currentIdName.Name });
    }
  }
  return idNameChildren;
}

export function addParentMappingToTreeData(
  allTreeNodes: Array<CustomTreeNode> | undefined,
  parentSearchText: string | undefined | null,
) {
  if (!allTreeNodes) {
    return;
  }
  for (let i = 0; i < allTreeNodes.length; i++) {
    allTreeNodes[i].SearchText = allTreeNodes[i].Name;
    if (parentSearchText) {
      allTreeNodes[i].SearchText += ` ${parentSearchText}`;
    }
    const childNodes = allTreeNodes[i].Children;
    if (childNodes === undefined || childNodes.length === 0) {
      continue;
    }
    addParentMappingToTreeData(childNodes, allTreeNodes[i].SearchText);
  }
}

export function getCleanEnumName(enumKey: string) {
  if (enumKey === undefined) {
    return "";
  }
  return toTitleCase(enumKey.replaceAll("_", " "));
}

export function getCleanNamesFromEnum(optionsEnum: any): Array<string> {
  return Object.keys(optionsEnum)
    .filter((v) => isNaN(Number(v)))
    .map((key) => getCleanEnumName(key));
}

export function getIdsFromEnum(optionsEnum: any): Array<number> {
  return Object.values(optionsEnum).filter((v) => !isNaN(Number(v))) as Array<number>;
}

export function getIdNamePairsFromEnum(
  optionsEnum: any,
  sortByName: boolean = false,
): Array<IdNamePair> {
  const idNamePairs: Array<IdNamePair> = [];
  const enumKeys = Object.keys(optionsEnum).filter((v) => isNaN(Number(v)));
  enumKeys.forEach((key) => {
    const cleanName = getCleanEnumName(key);
    idNamePairs.push({ Id: optionsEnum[key], Name: cleanName });
  });
  if (sortByName) {
    sortIdNamePairsByName(idNamePairs);
  }
  return idNamePairs;
}

export function getInputOptionsFromEnum(
  optionsEnum: any,
  sortByName: boolean = false,
): Array<InputOption> {
  const inputOptions: Array<InputOption> = [];
  const enumKeys = Object.keys(optionsEnum);
  enumKeys.forEach((key) => {
    const cleanName = getCleanEnumName(key);
    inputOptions.push({ Id: optionsEnum[key], Name: cleanName });
  });
  if (sortByName) {
    sortInputOptionsByName(inputOptions);
  }
  return inputOptions;
}

export function sortInputOptionsByName(inputOptions: Array<InputOption>) {
  inputOptions.sort(function (a, b) {
    return a.Name.toLowerCase().localeCompare(b.Name.toLowerCase());
  });
}

export function getIdNamePairsFromMatchingEnums(
  enum1: any,
  enum2: any,
  sortByName: boolean = false,
): Array<IdNamePair> {
  const idNamePairs: Array<IdNamePair> = [];
  const enum1Keys = Object.keys(enum1).filter((v) => isNaN(Number(v)));
  const enum2Keys = Object.keys(enum2).filter((v) => isNaN(Number(v)));
  const matchingKeys = enum1Keys.filter((key) => enum2Keys.includes(key));
  matchingKeys.forEach((key) => {
    idNamePairs.push({ Id: enum1[key], Name: enum2[key] });
  });
  if (sortByName) {
    sortIdNamePairsByName(idNamePairs);
  }
  return idNamePairs;
}

export function sortIdNamePairsByName(idNamePairs: Array<IdNamePair>) {
  return sortInputOptionsByName(idNamePairs);
}

export function getIdNamePairById(
  IdNamePairs: Array<IdNamePair> | null,
  id: number,
): IdNamePair | undefined {
  if (!IdNamePairs) {
    return undefined;
  }
  return IdNamePairs.find((pair) => pair.Id === id);
}

export function sortNullableValues(a: number | string | null, b: number | string | null) {
  if (a == b) {
    return 0;
  }
  if (a == null) {
    return 1;
  }
  if (b == null) {
    return -1;
  }
  return a > b ? 1 : -1;
}

export function getNameFromEnumId(id: number, optionsEnum: any): string {
  return getCleanEnumName(optionsEnum[id]);
}

export function getNameFromEnumString(id: string, optionsEnum: any): string | null {
  const matchingKey = Object.keys(optionsEnum).find(
    (key) => optionsEnum[key as keyof typeof optionsEnum] === id.trim(),
  );
  return matchingKey ? getCleanEnumName(matchingKey) : null;
}

export function getNameFromMatchingEnums(id: number, idsEnum: any, namesEnum: any): string {
  const matchingKey = Object.keys(idsEnum).find(
    (key) => idsEnum[key as keyof typeof idsEnum] === id,
  );
  return matchingKey ? namesEnum[matchingKey as keyof typeof namesEnum] : "";
}

export function toTitleCase(str: string): string {
  return str.toLowerCase().replace(/(^|\s)\S/g, (firstLetter) => firstLetter.toUpperCase());
}

export function splitStringByUpperCase(str: string): string {
  const strParts = str.match(/[A-Z][a-z]+/g);
  return strParts ? strParts.join(" ") : str;
}

export function addDays(date: Date, days: number) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export function getFormattedDate(dateString: string, dateFormat: string = "dd MMM yyyy") {
  return format(getClientSideIstTime(dateString), dateFormat);
}

/**
 * Single source of truth for batch expiry (BR-UI-029/033): a batch is expired
 * when its ISO expiry date is in the past. Null/undefined ⇒ not expired.
 */
export function isBatchExpired(expiryDate: string | null | undefined): boolean {
  if (!expiryDate) return false;
  // Date-only expiry: a batch stays usable through the whole of its expiry day, so it's expired only
  // once that day has fully passed. Parse the Y-M-D as a LOCAL end-of-day — `new Date("2026-07-14")`
  // parses as UTC midnight, which would flip a batch to "expired" at 05:30 IST, a day-boundary
  // mismatch with the IST display (LOB-11).
  const [year, month, day] = expiryDate.slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return false;
  const endOfExpiryDay = new Date(year, month - 1, day, 23, 59, 59, 999);
  return endOfExpiryDay.getTime() < Date.now();
}

export function getFormattedDateTime(datetimeString: string) {
  return format(getClientSideIstTime(datetimeString), "dd MMM h:mmaaaaa'm'");
}

export function getFormattedDateTimeTz(datetimeTz: string) {
  const nativePart = datetimeTz.replace(/([+-]\d{2}:?\d{2}|Z)$/, "");
  return getFormattedDate(nativePart, "dd MMM h:mm a");
}

export function getFormattedTime(datetimeString: string) {
  return format(getClientSideIstTime(datetimeString), "HH:mm");
}

// Normalizes an ion-datetime value (full ISO datetime OR bare "HH:mm[:ss]") to a
// time-only "HH:mm:ss" string — the shape backend `time` fields expect (B12/B13).
export function getISOTimeString(datetimeOrTimeString: string) {
  const timeOnlyMatch = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(datetimeOrTimeString);
  if (timeOnlyMatch) {
    return `${timeOnlyMatch[1]}:${timeOnlyMatch[2]}:${timeOnlyMatch[3] ?? "00"}`;
  }
  return format(parseISO(datetimeOrTimeString), "HH:mm:ss");
}

export function getDateTimeString(datetime: Date) {
  return format(datetime, "yyyy-MM-dd HH:mm");
}

export function getDisplayMonth(datetime: string) {
  return getDateString(new Date(datetime), "MMMM yyyy");
}

export function getDateString(datetime: Date, dateFormat: string = "yyyy-MM-dd") {
  return format(datetime, dateFormat);
}

export function getISODateString(datetime: Date | undefined) {
  return datetime ? format(datetime, ISODateFormat) : undefined;
}

export function getTimeString(datetime: Date, timeFormat: string = "HH:mm:ss") {
  return format(datetime, timeFormat);
}

export function getClientSideIstTime(datetime: string) {
  return parseISO(datetime);
}

export function getIstTimeFromTimestamp(timestamp: number) {
  return new Date(
    timestamp - getTimezoneOffset(Intl.DateTimeFormat().resolvedOptions().timeZone, new Date()),
  );
}

export function getTimestampFromDatetime(datetime: string) {
  return (
    fromZonedTime(datetime, import.meta.env.VITE_TIME_ZONE).valueOf() +
    getTimezoneOffset(import.meta.env.VITE_TIME_ZONE, new Date())
  );
}

export function getDatesInMonth(currentDate: Date): Array<Date> {
  const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const days = [];
  while (date.getMonth() === currentDate.getMonth()) {
    days.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }
  return days;
}

export function getStartDateOfWeek(datetime: string) {
  return startOfWeek(new Date(datetime));
}

export function getEndDateOfWeek(datetime: string) {
  return endOfWeek(new Date(datetime));
}

export function getFormattedDuration(
  timeInSeconds: number,
  includeZeroHours: boolean = true,
): string {
  const duration = intervalToDuration({ start: 0, end: timeInSeconds * 1000 });
  const zeroPad = (n: number | undefined) => (n ? n : 0).toString().padStart(2, "0");
  if (duration.days) {
    duration.hours = duration.days * 24 + (duration.hours ? duration.hours : 0);
  }
  const formattedDuration =
    includeZeroHours || duration.hours
      ? [duration.hours, duration.minutes, duration.seconds]
      : [duration.minutes, duration.seconds];
  return formattedDuration.map(zeroPad).join(":");
}

export function formatTimestamp(timestamp: string) {
  if (!timestamp) {
    return "00:00";
  }
  return timestamp.split(".")[0].split(",")[0];
}

export function timestampToSeconds(timestamp: string): number {
  if (!timestamp) {
    return 0;
  }
  const normTimestamp = timestamp.replace(",", ".");
  const parts = normTimestamp.split(":");
  if (parts.length === 3) {
    return parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseFloat(parts[2]);
  } else if (parts.length === 2) {
    return parseInt(parts[0]) * 60 + parseFloat(parts[1]);
  }
  return 0;
}

export function getFormattedDurationWithUnits(
  timeInSeconds: number,
  includeDays: boolean = false,
  isSmall: boolean = false,
  addSpaces: boolean = true,
): string {
  const duration = intervalToDuration({ start: 0, end: timeInSeconds * 1000 });
  if (!includeDays && duration.days) {
    duration.hours = duration.days * 24 + (duration.hours ? duration.hours : 0);
  }

  const formattedDuration = [];
  if (includeDays && duration.days) {
    let daysUnit = isSmall ? "d" : duration.days > 1 ? "days" : "day";
    daysUnit = addSpaces ? ` ${daysUnit}` : daysUnit;
    formattedDuration.push(`${duration.days}${daysUnit}`);
  }
  if (duration.hours) {
    let hoursUnit = isSmall ? "h" : duration.hours > 1 ? "hrs" : "hour";
    hoursUnit = addSpaces ? ` ${hoursUnit}` : hoursUnit;
    formattedDuration.push(`${duration.hours}${hoursUnit}`);
  }
  if (duration.minutes) {
    let minutesUnit = isSmall ? "m" : duration.minutes > 1 ? "mins" : "min";
    minutesUnit = addSpaces ? ` ${minutesUnit}` : minutesUnit;
    formattedDuration.push(`${duration.minutes}${minutesUnit}`);
  }
  if (duration.seconds) {
    let secondsUnit = isSmall ? "s" : duration.seconds > 1 ? "secs" : "sec";
    secondsUnit = addSpaces ? ` ${secondsUnit}` : secondsUnit;
    formattedDuration.push(`${duration.seconds}${secondsUnit}`);
  }
  return formattedDuration.slice(0, 2).join(" ");
}

export function getDaysGapString(dateString: string, currentDate: Date) {
  const inputDate = new Date(dateString);
  inputDate.setHours(0, 0, 0, 0);
  currentDate.setHours(0, 0, 0, 0);
  const daysGap = differenceInDays(inputDate, currentDate);
  return daysGap < 0
    ? `${Math.abs(daysGap)} days ago`
    : daysGap > 0
      ? `+${Math.abs(daysGap)} days later`
      : "Today";
}

export function getFormattedNumber(decimalValue: number, precision: number) {
  return Number(decimalValue).toLocaleString(undefined, {
    style: "decimal",
    maximumFractionDigits: precision,
  });
}

export function getFormattedPercentage(percentValue: number, precision: number) {
  return Number(percentValue).toLocaleString(undefined, {
    style: "percent",
    minimumFractionDigits: precision,
  });
}

export function getNMoreIdNames(idNames: Array<IdNamePair> | undefined) {
  if (!idNames) {
    return "";
  }
  return getNMoreString(idNames.map((idName) => idName.Name));
}

export function getNMoreString(stringValues: string | Array<string> | undefined) {
  if (!stringValues) {
    return "";
  } else if (Array.isArray(stringValues)) {
    // An empty list returned stringValues[0] === undefined, which leaked out to required
    // String props as a missing-prop warning. "" keeps the falsy contract callers v-if on.
    if (!stringValues.length) {
      return "";
    } else if (stringValues.length > 1) {
      return `${stringValues[0]} +${stringValues.length - 1} more`;
    } else {
      return stringValues[0];
    }
  } else {
    return stringValues;
  }
}

export function emptyValueFormatter(inputValue: number | string | null | undefined) {
  return inputValue ? inputValue.toString() : "-";
}

export function getDateRangeDisplay(dateRange: Array<string>) {
  if (!dateRange) {
    return null;
  }
  let startDate = getFormattedDate(dateRange[0]);
  let endDate = getFormattedDate(dateRange[1]);
  if (startDate === endDate) {
    return startDate;
  }
  const commonSubstring = commonTrailingSubstring(startDate, endDate);
  const commonSpaceIndex = commonSubstring.indexOf(" ");
  const cutoffSubstring = commonSubstring.substring(commonSpaceIndex).trim();
  startDate = startDate.replace(cutoffSubstring, "").trim();
  endDate = endDate.replace(cutoffSubstring, "").trim();
  return `${startDate} - ${endDate} ${cutoffSubstring}`;
}

export function commonTrailingSubstring(string1: string, string2: string) {
  let i = string1.length;
  let j = string2.length;
  while (i >= 0 && j >= 0 && string1.charAt(i) === string2.charAt(j)) {
    i--;
    j--;
  }
  return string1.substring(i + 1);
}

export function getContentArray(contentValue: number | Array<number> | null) {
  return Array.isArray(contentValue) ? contentValue : contentValue === null ? [] : [contentValue];
}

export async function showCustomMessage(messageDetails: CustomMessage) {
  if (messageDetails.messageText === undefined) {
    return;
  }
  if (
    messageDetails.duration !== undefined &&
    (messageDetails.duration === 0 || messageDetails.duration >= 5)
  ) {
    messageDetails.showClose = true;
  }
  const sessionStore = useSessionStore();
  const { ActiveCustomToasts } = storeToRefs(sessionStore);
  const toast = await presentToast({
    toastIndex: ActiveCustomToasts.value.length,
    messageText: messageDetails.messageText,
    toastColor: messageDetails.messageType ?? "tertiary",
    duration: messageDetails.duration ?? 5,
    showClose: !!messageDetails.showClose,
    position: "top",
  });
  if (!messageDetails.isSticky) {
    ActiveCustomToasts.value.push(toast);
  }
}

export async function presentToast(toastDetails: CustomToast): Promise<HTMLIonToastElement> {
  const duration = (toastDetails.duration !== undefined ? toastDetails.duration : 10) * 1000;
  const position = toastDetails.position ? toastDetails.position : "top";
  const toastColor = toastDetails.toastColor ? toastDetails.toastColor : "tertiary";
  const buttons = [];
  if (toastDetails.showClose) {
    buttons.push({ icon: close, htmlAttributes: { "aria-label": "close" } });
  }
  let cssClass = `custom-ion-toast ${toastColor} toast-${toastDetails.toastIndex}`;
  if (toastDetails.customClass) {
    cssClass += ` ${toastDetails.customClass}`;
  }
  const toast = await toastController.create({
    message: toastDetails.messageText,
    icon:
      !toastDetails.iconName || toastDetails.iconName.includes("/")
        ? toastDetails.iconName
        : getIconUrl(toastDetails.iconName),
    duration: duration,
    position: position,
    positionAnchor: toastDetails.positionAnchorId ? toastDetails.positionAnchorId : undefined,
    cssClass: cssClass,
    buttons: buttons,
    swipeGesture: "vertical",
  });
  await toast.present();
  return toast;
}

const LOADING_MESSAGES = {
  student: [
    "Education is the most powerful weapon you can use to change the world.",
    "The beautiful thing about learning is that no one can take it away from you.",
    "Every expert was once a beginner. Keep going!",
    "Your future is created by what you do today, not tomorrow.",
    "Learning is a treasure that will follow its owner everywhere.",
  ],
  teacher: [
    "Teachers plant seeds of knowledge that grow forever.",
    "A teacher's influence lasts a lifetime.",
    "Behind every successful student is a dedicated teacher.",
    "Teaching is the profession that creates all other professions.",
    "One teacher can change a student's life forever.",
  ],
  fallback: [
    "Good things take a moment. Almost there!",
    "Excellence takes time. Preparing your experience…",
    "Loading something great for you!",
  ],
} as const;

function getRoleBasedLoadingMessage(): string {
  const roleId = useSessionStore().UserInfo.RoleId;
  let pool: readonly string[];
  if ((AllStudentRoleIds as readonly number[]).includes(roleId)) {
    pool = LOADING_MESSAGES.student;
  } else if ((AllTeachingRoleIds as readonly number[]).includes(roleId)) {
    pool = LOADING_MESSAGES.teacher;
  } else {
    pool = LOADING_MESSAGES.fallback;
  }
  return pool[Math.floor(Math.random() * pool.length)];
}

export function withLoading<TArgs extends unknown[], TReturn>(
  awaitableFunction: (...args: TArgs) => Promise<TReturn>,
  useRoleBasedMessage: boolean = false,
): (...args: TArgs) => Promise<TReturn> {
  return async function (...args: TArgs): Promise<TReturn> {
    const loader = await loadingController.create({
      spinner: "crescent",
      message: useRoleBasedMessage ? getRoleBasedLoadingMessage() : "Data loading",
      backdropDismiss: false,
      cssClass: "custom-ion-loading",
    });
    await loader.present();
    try {
      return await awaitableFunction(...args);
    } finally {
      await loader.dismiss();
    }
  };
}

let uniqueDomIdCounter = 0;
// For components whose element ids are resolved by document-global lookups (Ionic
// segment-view contents, ion-menu content/menu ids). Cached router-outlet pages keep
// their DOM alive, so a static id collides with the hidden copy and the lookup binds
// to the wrong element — ids must be unique per component instance.
export function getUniqueDomIdPrefix(baseName: string): string {
  uniqueDomIdCounter += 1;
  return `${baseName}-${uniqueDomIdCounter}-`;
}

// Segment values are user-facing labels (e.g. "Student Wise") but HTML ids must not
// contain whitespace. The segment button's content-id and the segment content's id
// must both be built through this helper so the pair always matches.
export function getSegmentContentId(prefix: string, segmentValue: string): string {
  return `${prefix}${segmentValue}`.replace(/\s+/g, "-");
}

export async function showCustomNotification(
  messageDetails: CustomNotification,
  defaultDuration: number = 10,
) {
  if (messageDetails.messageTitle === undefined || messageDetails.messageText === undefined) {
    return;
  }
  messageDetails.duration =
    messageDetails.duration !== undefined ? messageDetails.duration : defaultDuration;
  if (messageDetails.duration >= 10) {
    messageDetails.showClose = true;
  }
  const sessionStore = useSessionStore();
  const { ActiveCustomToasts } = storeToRefs(sessionStore);
  const position = NotificationPositionMap[messageDetails.messagePosition ?? "bottom"] ?? "bottom";
  const toast = await presentToast({
    toastIndex: ActiveCustomToasts.value.length,
    messageText: `${messageDetails.messageTitle}\n${messageDetails.messageText}`,
    toastColor: messageDetails.messageType,
    iconName: messageDetails.messageIcon,
    duration: messageDetails.duration,
    showClose: !!messageDetails.showClose,
    position: position,
    customClass: `custom-notification-${messageDetails.messageType}`,
  });
  ActiveCustomToasts.value.push(toast);
  return toast;
}

const CustomToastDurationDefaults: Record<ComponentColorType, number> = {
  primary: 3,
  success: 3,
  secondary: 3,
  tertiary: 3,
  medium: 3,
  warning: 10,
  danger: 10,
};

export async function showCustomToast(
  type: ComponentColorType,
  details: { messageText: string; duration?: number; showClose?: boolean; isSticky?: boolean },
): Promise<HTMLIonToastElement> {
  const sessionStore = useSessionStore();
  const { ActiveCustomToasts } = storeToRefs(sessionStore);
  const toast = await presentToast({
    toastIndex: ActiveCustomToasts.value.length,
    messageText: details.messageText,
    toastColor: type,
    duration: details.duration ?? CustomToastDurationDefaults[type],
    showClose: details.showClose,
    position: "top",
  });
  if (!details.isSticky) {
    ActiveCustomToasts.value.push(toast);
  }
  return toast;
}

export function getCleanFileName(fileName: string): string {
  return filenamify(fileName, { replacement: "-" });
}

export async function compressImage(fileBlob: Blob, maxFileSizeMB: number = MaxUploadImageSizeMB) {
  try {
    if (fileBlob.size / 1024 / 1024 <= maxFileSizeMB) {
      return fileBlob;
    }
    const options = { maxSizeMB: maxFileSizeMB, useWebWorker: true };
    return await imageCompression(fileBlob as File, options);
  } catch (error) {
    console.debug(`Error during image compression: ${error}`);
  }
}

export async function downloadCsvData(
  objectData: Array<any>,
  objectKeys: Array<string> | null,
  fileName: string,
) {
  const flattenedData = objectData.map((o) => flattenObject(o));
  let objectHeaders = Object.keys(flattenedData[0]);
  if (objectKeys !== null) {
    objectHeaders = objectKeys.filter((ok) => objectHeaders.includes(ok));
  }
  const objectRows = flattenedData.map((o) => objectHeaders.map((h) => o[h]));
  const escapedRows = objectRows.map((row) => {
    const newRow = row.map((col) => {
      return col === undefined || col === null ? "" : `"${col}"`;
    });
    return newRow;
  });
  const headerRow = objectHeaders.join(",");
  const csvRows = [headerRow, ...escapedRows.map((row) => row.join(","))];
  const csvData = csvRows.join("\n");
  const cleanFileName = getCleanFileName(`${fileName}.csv`);
  const appSettingsStore = useAppSettingsStore();
  const { IsNativePlatform } = storeToRefs(appSettingsStore);
  if (IsNativePlatform.value) {
    await downloadFileToNativePlatform(csvData, cleanFileName);
  } else {
    downloadFile(csvData, `${MimeTypesEnum.CSV};charset=utf-8`, cleanFileName);
  }
}

export async function downloadPngFile(
  fileUrl: string,
  fileName: string,
  randomize: boolean = false,
) {
  await downloadFileFromUrl(fileUrl, MimeTypesEnum.PNG, fileName, randomize);
}

export async function downloadJpgFile(
  fileUrl: string,
  fileName: string,
  randomize: boolean = false,
) {
  await downloadFileFromUrl(fileUrl, MimeTypesEnum.JPEG, fileName, randomize);
}

export async function downloadCsvFile(
  csvUrl: string,
  fileName: string,
  randomize: boolean = false,
) {
  await downloadFileFromUrl(csvUrl, MimeTypesEnum.CSV, fileName, randomize);
}

export async function downloadPdf(pdfUrl: string, fileName: string, randomize: boolean = false) {
  await downloadFileFromUrl(pdfUrl, MimeTypesEnum.PDF, fileName, randomize);
}

const PDF_FILE_EXTENSION = ".pdf";
const DEFAULT_PDF_FILE_NAME = "document";

// The name a PDF download is saved under: the first non-empty candidate (a viewer's explicit
// name, then its modal title, then the URL's own file name), always with a .pdf extension so
// the browser never saves an extension-less blob. A data: URI has no usable file name, so it
// falls through to the default.
export function getPdfDownloadFileName(...candidates: Array<string | null | undefined>): string {
  const preferred = candidates.find((candidate) => (candidate ?? "").trim() !== "")?.trim();
  const baseName = preferred ?? DEFAULT_PDF_FILE_NAME;
  return baseName.toLowerCase().endsWith(PDF_FILE_EXTENSION)
    ? baseName
    : `${baseName}${PDF_FILE_EXTENSION}`;
}

// The file-name segment of a URL (no query string), or "" when the URL has none — a data:
// URI, a bare origin — so callers can offer it as a download-name candidate.
export function getUrlFileName(url: string): string {
  if (!url || url.startsWith("data:")) {
    return "";
  }
  const path = url.split("?")[0].split("#")[0];
  const segment = path.substring(path.lastIndexOf("/") + 1);
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

export async function downloadZip(
  zipUrl: string,
  fileName: string,
  randomize: boolean = false,
  percentCompleted: Ref<number> | undefined = undefined,
) {
  await downloadFileFromUrl(zipUrl, MimeTypesEnum.ZIP, fileName, randomize, percentCompleted);
}

export async function downloadFileFromUrl(
  fileUrl: string,
  fileType: string,
  fileName: string,
  randomize: boolean = false,
  percentCompleted: Ref<number> | undefined = undefined,
): Promise<boolean> {
  fileUrl = getAzureUrl(fileUrl);
  if (randomize) {
    const connector = fileUrl.includes("?") ? "&" : "?";
    fileUrl = `${fileUrl}${connector}random=${Math.random().toString()}`;
  }
  const cleanFileName = getCleanFileName(fileName);
  if (percentCompleted === undefined) {
    percentCompleted = ref(0);
  }
  try {
    const response = await axios.get(fileUrl, {
      responseType: "arraybuffer",
      onUploadProgress: (event) => {
        percentCompleted.value = event.total ? Math.round((event.loaded * 100) / event.total) : 0;
      },
      onDownloadProgress: (event) => {
        percentCompleted.value = event.total ? Math.round((event.loaded * 100) / event.total) : 0;
      },
    });
    const appSettingsStore = useAppSettingsStore();
    const { IsNativePlatform } = storeToRefs(appSettingsStore);
    if (IsNativePlatform.value) {
      await downloadFileToNativePlatform(response.data, cleanFileName);
    } else {
      downloadFile(response.data, fileType, cleanFileName);
    }
  } catch (error) {
    console.error("Error downloading file:", error);
    showCustomMessage({
      messageText: "Oops! There was an error downloading the file.",
      messageType: "danger",
    });
    return false;
  }
  return true;
}

function downloadFile(fileData: BlobPart, fileType: string, fileName: string) {
  const fileBlob = new Blob([fileData], { type: fileType });
  const downloadLink = document.createElement("a");
  const downloadHref = URL.createObjectURL(fileBlob);
  downloadLink.href = downloadHref;
  downloadLink.setAttribute("download", fileName);
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
  URL.revokeObjectURL(downloadHref);
}

async function downloadFileToNativePlatform(fileData: BlobPart | string, fileName: string) {
  const appSettingsStore = useAppSettingsStore();
  const { DevicePlatform } = storeToRefs(appSettingsStore);
  const isAndroid = DevicePlatform.value === DevicePlatformsEnum.ANDROID;
  if (isAndroid) {
    const filePermission = await Filesystem.checkPermissions();
    if (filePermission.publicStorage !== "granted") {
      const permissionResponse = await Filesystem.requestPermissions();
      if (permissionResponse.publicStorage !== "granted") {
        showCustomMessage({
          messageText: "Storage permission is required to download files.",
          messageType: "warning",
        });
        return;
      }
    }
  }
  try {
    const fileBlob = new Blob([fileData], { type: "application/octet-stream" });
    const dataUrl = await convertBlobToBase64(fileBlob);
    if (!dataUrl) {
      showCustomMessage({
        messageText: "Unable to process the file for download.",
        messageType: "warning",
      });
      return;
    }
    const base64Data = dataUrl.split(",")[1];
    try {
      await Filesystem.writeFile({
        path: `Download/${fileName}`,
        data: base64Data,
        directory: Directory.ExternalStorage,
        recursive: true,
      });
      showCustomMessage({
        messageText: `Saved "${fileName}" to Downloads.`,
        messageType: "success",
      });
    } catch (downloadError: any) {
      console.debug(downloadError);
      await Filesystem.writeFile({
        path: fileName,
        data: base64Data,
        directory: Directory.Documents,
        recursive: true,
      });
      showCustomMessage({
        messageText: `Saved "${fileName}" to Documents.`,
        messageType: "success",
      });
    }
  } catch (fsError: any) {
    console.error("Filesystem error:", fsError);
    showCustomMessage({
      messageText: "An error occured when downloading the file.",
      messageType: "danger",
    });
  }
}

export async function downloadFileForOfflineAccess(
  fileUrl: string,
  fileName: string,
  randomize: boolean = false,
  percentCompleted: Ref<number> | undefined = undefined,
) {
  fileUrl = getAzureUrl(fileUrl);
  if (randomize) {
    const connector = fileUrl.includes("?") ? "&" : "?";
    fileUrl = `${fileUrl}${connector}random=${Math.random().toString()}`;
  }
  const fileInfo = await Filesystem.getUri({ directory: Directory.Data, path: fileName });
  try {
    await FileTransfer.downloadFile({ url: fileUrl, path: fileInfo.uri, progress: true });
    FileTransfer.addListener("progress", (progress) => {
      if (percentCompleted && progress.lengthComputable) {
        percentCompleted.value = (progress.bytes / progress.contentLength) * 100;
      }
    });
  } catch (error: any) {
    console.error("Filesystem error:", error);
    showCustomMessage({
      messageText: "An error occured when downloading the file.",
      messageType: "danger",
    });
  }
}

function flattenObject(nestedObject: any, prefix = "") {
  const flattenedObject: any = {};
  Object.keys(nestedObject).forEach((key) => {
    if (typeof nestedObject[key] === "object" && nestedObject[key] !== null) {
      Object.assign(flattenedObject, flattenObject(nestedObject[key], key));
    } else {
      const flatKey = prefix ? `${prefix}.${key}` : key;
      flattenedObject[flatKey] = nestedObject[key];
    }
  });
  return flattenedObject;
}

export async function getFileMd5CheckSum(fileBytes: Blob) {
  const spark = new SparkMD5.ArrayBuffer();
  const buffer = await fileBytes.arrayBuffer();
  spark.append(buffer);
  return spark.end();
}

export async function uploadFileToAzure(fileBlob: Blob, uploadUrl: AzureUploadUrl) {
  try {
    const partialUrl = uploadUrl.url;
    const fullUrl = getAzureUrl(partialUrl, false, false);
    const headers = {
      "x-ms-blob-type": "BlockBlob",
      "Content-Type": fileBlob.type,
    };
    const config = { headers: headers };
    await axios.put(`${fullUrl}?${uploadUrl.sasToken}`, fileBlob, config);
  } catch (error) {
    console.error("Error uploading file to Azure Blob Storage:", error);
    return false;
  }
  return true;
}

export async function getEncodedUploadFile(uploadFile: Array<UploaderFile> | undefined) {
  if (!uploadFile || uploadFile.length === 0) {
    return;
  }
  const allEncodedFiles = await getEncodedUploadFiles([uploadFile[0]]);
  return allEncodedFiles ? allEncodedFiles[0] : undefined;
}

export async function getEncodedUploadFiles(uploadFiles: Array<UploaderFile>) {
  if (!uploadFiles || uploadFiles.length === 0) {
    return;
  }
  const allEncodedFiles: Array<string> = [];
  for (let i = 0; i < uploadFiles.length; i++) {
    const fileContent = uploadFiles[i].raw;
    if (fileContent) {
      try {
        const encodedFile = await convertBlobToBase64(fileContent);
        if (encodedFile) {
          allEncodedFiles.push(encodedFile);
        }
      } catch (err) {
        console.error(err);
      }
    }
  }
  return allEncodedFiles;
}

export async function readFilePath(filePath: string): Promise<Blob | undefined> {
  return new Promise((resolve, reject) => {
    fetch(filePath)
      .then(async (response) => resolve(response.blob()))
      .catch((error) => {
        reject(`Failed to access the file: ${error}`);
      });
  });
}

export async function readFilePathToBase64(filePath: string): Promise<string | undefined> {
  return new Promise((resolve, reject) => {
    fetch(filePath)
      .then((response) => response.blob())
      .then(async (blob) => {
        const base64Url = await convertBlobToBase64(blob);
        resolve(base64Url);
      })
      .catch((error) => {
        reject(`Failed to access the file: ${error}`);
      });
  });
}

export function convertBase64ToBlob(base64String: string): Blob {
  const parts = base64String.split(",");
  const mimeType = parts[0].match(/:(.*?);/)?.[1] || MimeTypesEnum.JPEG;
  const base64Data = parts[1];
  const binaryString = atob(base64Data);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeType });
}

export async function convertBlobToBase64(fileContent: Blob): Promise<string | undefined> {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(fileContent);
    fileReader.onload = async () => {
      resolve(fileReader.result ? fileReader.result.toString() : undefined);
    };
    fileReader.onerror = (error) => {
      reject(error);
    };
  });
}

export function isBase64Data(inputString: string | null | undefined): boolean {
  if (inputString === null || inputString === undefined) {
    return false;
  }
  return inputString.startsWith("data:");
}

export function areObjectsEqual(obj1: any, obj2: any): boolean {
  return _.isEqual(obj1, obj2);
}

export function deepCopyObject(obj: object): object {
  return JSON.parse(JSON.stringify(obj));
}

export function deepCopyDate(date: Date): Date {
  const deepCopy = deepCopyObject(date) as any;
  return new Date(deepCopy);
}

export function getUniqueObjectsByKey(objectArray: Array<object>, key: string): Array<object> {
  const objKey = key as keyof (typeof objectArray)[0];
  return [...new Map(objectArray.map((item) => [item[objKey], item])).values()];
}

export function generateRandomString(stringLength: number) {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < stringLength; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

export function getProgressBarColor(progressValue: number) {
  progressValue = Math.max(
    Math.min(progressValue, ProgressThresholds[ProgressThresholds.length - 1]),
    ProgressThresholds[0],
  );
  const closestNextIndex = ProgressThresholds.findIndex((n) => n >= progressValue);
  return closestNextIndex >= 0 && closestNextIndex < ProgressColors.length
    ? ProgressColors[closestNextIndex]
    : DefaultProgressColor;
}

// Class-based sibling to getProgressBarColor. Returns a single class
// ("bg-color-<name>-dark") that renders the saturated fill color — the `-dark`
// suffix is required because `.bg-color-<name>` alone produces a near-white
// background (see src/assets/styles/colors.scss). Used by CustomProgressBar's
// Tailwind-only rewrite (Task 2.3). Threshold boundaries match ProgressThresholds.
export function getProgressBarColorClass(progressValue: number): string {
  const clamped = Math.max(
    Math.min(progressValue, ProgressThresholds[ProgressThresholds.length - 1]),
    ProgressThresholds[0],
  );
  const closestNextIndex = ProgressThresholds.findIndex((n) => n >= clamped);
  const name =
    closestNextIndex >= 0 && closestNextIndex < ProgressColorNames.length
      ? ProgressColorNames[closestNextIndex]
      : DefaultProgressColorName;
  return `bg-color-${name}-dark`;
}

// Generic sibling to getProgressBarColorClass: maps a color-token name to the
// saturated `-dark` fill class (see src/assets/styles/colors.scss). Keeps the
// `-dark` fill convention in one place for any dot/pill that needs a solid fill.
export function getColorFillClass(color: ComponentColorType): string {
  return `bg-color-${color}-dark`;
}

export function isValidHttpsUrl(url: string) {
  const urlPattern =
    /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/;
  return urlPattern.test(url);
}

export function isValidMobileNumber(mobileNumber: string) {
  const regex = /^(\+\d{1,3}[- ]?)?\d{10}$/;
  return regex.test(mobileNumber);
}

export function isValidIndianPincode(pincode: string | null | undefined) {
  const regex = /^[1-9]{1}[0-9]{2}\s{0,1}[0-9]{3}$/;
  if (pincode === null || pincode === undefined) {
    return false;
  }
  return regex.test(pincode);
}

export function isValidEmail(email: string) {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
}

export function getInitialFormValues(
  cardData: any,
  yupSchema: any,
  fieldToNestedObjectMap: { [key: string]: string } | undefined = undefined,
  additionalProps: { [key: string]: any } | undefined = undefined,
) {
  const fieldNames: Array<string> = Object.keys(yupSchema.fields);
  const initialValues: { [key: string]: any } = {};
  for (const field of fieldNames) {
    if (fieldToNestedObjectMap && field in fieldToNestedObjectMap) {
      const nestedKey = fieldToNestedObjectMap[field as keyof typeof fieldToNestedObjectMap];
      const idNamePair = (cardData as any)[nestedKey as keyof any];
      initialValues[field] = idNamePair ? idNamePair.Id : null;
    } else if (additionalProps && field in additionalProps) {
      initialValues[field] = additionalProps[field];
    } else if (field in cardData) {
      initialValues[field] = (cardData as any)[field];
    }
  }
  return initialValues;
}

export function getMinimumPageNumber(
  totalItems: number,
  pageSize: number,
  currentPageNumber: number,
) {
  const totalPages = Math.ceil(totalItems / pageSize);
  if (currentPageNumber > totalPages && totalPages > 0) {
    return totalPages;
  }
  return currentPageNumber;
}

export function initializeBackButton() {
  const currentRoute = useRoute();
  const appSettingsStore = useAppSettingsStore();
  const sessionStore = useSessionStore();
  const { AppMenu, ShowAppExitAlert } = storeToRefs(appSettingsStore);
  const { onExitApp } = appSettingsStore;
  let exitAlertCount = 0;
  let exitAlertShowTimeout: NodeJS.Timeout | null = null;
  ShowAppExitAlert.value = false;
  useBackButton(BackButtonPriority, () => {
    // usermgmt-8-2 (BR-UI-107) — trap hardware back to a no-op while the forced gate is active;
    // OS app-close is still allowed (we do not intercept it). Mirrors meta.overrideBackButton.
    if (currentRoute.meta.overrideBackButton === true || sessionStore.IsMustChangePasswordActive) {
      return;
    }
    if (AppMenu.value !== null && currentRoute.name === AppMenu.value.DefaultView) {
      exitAlertCount += 1;
      if (exitAlertCount >= ExitAlertCount) {
        onExitApp();
        return;
      } else {
        ShowAppExitAlert.value = true;
        if (exitAlertShowTimeout) {
          clearTimeout(exitAlertShowTimeout);
        }
        exitAlertShowTimeout = setTimeout(() => {
          ShowAppExitAlert.value = false;
          exitAlertCount = 0;
        }, 30000);
        return;
      }
    } else {
      gotoPrevious();
      return;
    }
  });
}

export function addKeyboardClickListener(hideOnEnter: boolean = true) {
  const appSettingsStore = useAppSettingsStore();
  const { IsNativePlatform } = storeToRefs(appSettingsStore);
  if (!IsNativePlatform.value) {
    return;
  }
  window.addEventListener("keydown", (event: KeyboardEvent) => {
    if (hideOnEnter && event.key === "Enter") {
      Keyboard.hide();
    }
  });
}

export function addArtificialDelay(delayInSeconds: number) {
  return new Promise((resolve) => setTimeout(resolve, delayInSeconds * 1000));
}

export function getFormattedFileSize(fileSizeInBytes: number): string {
  if (fileSizeInBytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(fileSizeInBytes) / Math.log(1024));
  const size = (fileSizeInBytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0);
  return `${size} ${units[i]}`;
}

export async function mergeBase64Images(base64Images: Array<string>): Promise<string> {
  if (base64Images.length === 1) {
    return base64Images[0];
  }
  return new Promise((resolve) => {
    const images: HTMLImageElement[] = [];
    let loadedCount = 0;
    let totalWidth = 0;
    let totalHeight = 0;
    const onImageLoad = (img: HTMLImageElement) => {
      loadedCount += 1;
      totalWidth = Math.max(totalWidth, img.width);
      totalHeight += img.height;
      if (loadedCount === base64Images.length) {
        const canvas = document.createElement("canvas");
        canvas.width = totalWidth;
        canvas.height = totalHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          return;
        }
        let currentY = 0;
        images.forEach((img) => {
          ctx.drawImage(img, 0, currentY, img.width, img.height);
          currentY += img.height;
        });
        const mergedBase64 = canvas.toDataURL(MimeTypesEnum.PNG);
        resolve(mergedBase64);
      }
    };
    base64Images.forEach((base64) => {
      const img = new Image();
      img.onload = () => onImageLoad(img);
      img.src = base64;
      images.push(img);
    });
  });
}

export function areValidArraySubset(parentArray: Array<any>, childArray: Array<any>): boolean {
  return childArray.every((value) => parentArray.includes(value));
}

export function getRegistrationDeviceTypeId(): LoginDeviceTypeIdsEnum {
  const appSettingsStore = useAppSettingsStore();
  const { IsNativePlatform } = storeToRefs(appSettingsStore);
  return IsNativePlatform.value ? LoginDeviceTypeIdsEnum.MOBILE : LoginDeviceTypeIdsEnum.WEB;
}

export function getMobileNumberYupRule() {
  return yup
    .string()
    .required("Mobile number is required")
    .matches(/^\d{10}$/, "Enter a valid 10-digit mobile number");
}

export function getNewPasswordYupRule(currentPassword: string) {
  return yup
    .string()
    .required("New password is required")
    .min(8, "Password must be at least 8 characters")
    .notOneOf([currentPassword], "New password must differ from the current password")
    .notOneOf(["abc123"], "New password cannot be the system default");
}

export function getPasswordStrengthTier(password: string): PasswordStrengthTier {
  if (password.length < 8) {
    return "weak";
  }
  let classCount = 0;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) {
    classCount += 1; // mixed case
  }
  if (/\d/.test(password)) {
    classCount += 1; // digit
  }
  if (/[^A-Za-z0-9]/.test(password)) {
    classCount += 1; // symbol
  }
  if (classCount >= 3) {
    return "strong";
  }
  if (classCount >= 1) {
    return "good";
  }
  return "fair";
}

/**
 * Derive up-to-two-letter uppercase initials from a person's name.
 * "Asha Rao" → "AR", "madhav" → "M".
 */
export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts
    .map((p) => p[0] ?? "")
    .join("")
    .toUpperCase();
}
